import { useEffect, useMemo, useState } from 'react';
import { EquipmentModel, PartManufacturerModel } from '../../data/types';
import { Label } from '../atoms/Label';
import { inspectionDB } from '../../data/database.structure';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

interface EquipmentCardProps {
  equipment: EquipmentModel;
  onClick?: () => void;
}

export const EquipmentCard = (props: EquipmentCardProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [manufacturer, setManufacturer] = useState<
    PartManufacturerModel | undefined
  >(undefined);
  const [equipmentConfiguration, setEquipmentConfiguration] = useState<
    string | undefined
  >(undefined);

  const isClickable = useMemo(
    () => props.onClick !== undefined,
    [props.onClick],
  );

  const classes = useMemo(
    () =>
      `w-full flex flex-col ${isClickable ? 'cursor-pointer hover:bg-brandLight/30' : ''}`,
    [isClickable],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const manufacturer = await inspectionDB.assetManufacturers.get(
          props.equipment.manufacturerId,
        );
        setManufacturer(manufacturer);
        const equipmentConfiguration =
          await inspectionDB.equipmentConfigurations.get(
            props.equipment.configurationId,
          );
        setEquipmentConfiguration(equipmentConfiguration?.name);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [props.equipment]);

  if (isLoading) {
    // Layout stability: reserve the card's real shape (title line + detail line)
    // instead of the old full-size centred spinner, whose collapse into a
    // two-line card shifted everything below it.
    return (
      <div className={classes} aria-busy="true">
        <Skeleton width={240} height={28} />
        <Skeleton width={300} height={20} className="mt-1" />
      </div>
    );
  }

  return (
    <div className={classes} onClick={props.onClick}>
      <Label titleType="largeHeader">{props.equipment.name}</Label>
      <div>
        {[
          manufacturer?.name,
          equipmentConfiguration !== 'Unknown' && equipmentConfiguration,
          props.equipment.equipmentCategory ?? props.equipment.equipmentType,
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
    </div>
  );
};
